import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Sparkles,
  Boxes,
  Tag,
  Truck,
  Clock,
  MapPin,
  Layers,
  Zap,
  PackageCheck,
  Briefcase,
  Sofa,
  Store,
  Timer,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";
import { getYvelinesCityPageBySlug } from "@/data/yvelinesCitiesContent";

const cityNameToSlug = (name) =>
  name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/'/g, "")
    .replace(/\s+/g, "-");

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à Houilles ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison à domicile
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Service de livraison de cartons de déménagement {city.aCity}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Parce que la vie {city.aCity} va à cent à l&apos;heure,
            notre service de livraison à domicile s&apos;adapte à votre
            rythme et prend en charge l&apos;acheminement complet de
            vos packs d&apos;emballage en s&apos;appuyant sur trois
            piliers stricts.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "La ponctualité au cœur du 78",
              body: "Nous planifions ensemble un créneau horaire précis et respecté pour vous éviter de perdre votre journée, que vous résidiez à Houilles ou dans les communes voisines comme Sartrouville, Carrières-sur-Seine, Bezons ou Maisons-Laffitte.",
              Icon: Clock,
            },
            {
              title: "La remise à plat et au sec",
              body: "L'intégralité de vos fournitures vous est livrée impeccablement propre, saine et hermétiquement protégée face aux intempéries, prête à être pliée instantanément.",
              Icon: ShieldCheck,
            },
            {
              title: "La réactivité face aux imprévus",
              body: "Un manque de cartons de dernière minute ? Nos stocks permanents nous permettent de vous réapprovisionner dans des délais record pour ne jamais bloquer votre élan.",
              Icon: Zap,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Kits tout-en-un ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Kits prêts à l&apos;emploi
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Des formules tout-en-un : pourquoi opter pour
            l&apos;efficacité de nos kits de déménagement ?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Finis les calculs compliqués et les oublis de dernière
            minute. Nos kits thématiques regroupent tout le matériel
            nécessaire selon la taille de votre logement pour vous
            simplifier la vie.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              step: "1",
              title: "L'utilité du kit pro",
              body: "Vous recevez en une seule fois la juste proportion de cartons (standards et lourds) et les accessoires de fermeture indispensables pour avancer sans coupure.",
              Icon: PackageCheck,
            },
            {
              step: "2",
              title: "L'exemple du Kit F3",
              body: "Ce pack clé en main associe 30 cartons renforcés, des rouleaux d'adhésif industriel, un pistolet dévidoir et du film bulle pour sécuriser tout votre salon.",
              Icon: Sofa,
            },
            {
              step: "3",
              title: "Découvrez notre gamme",
              body: "Du pack studio économique à la dotation massive pour pavillon familial, trouvez la formule exacte adaptée à vos pièces sur notre boutique.",
              Icon: Store,
            },
          ].map(({ step, title, body, Icon }) => (
            <div
              key={step}
              className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6 flex flex-col"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#237cb5]">
                  Avantage {step}
                </span>
              </div>
              <h3 className="mt-3 font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                {title}
              </h3>
              <p className="mt-2.5 text-[13.5px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/kits-cartons"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-houilles-cta-kits"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir tous nos kits
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-houilles-cta-shop"
          >
            <Tag className="h-4 w-4" strokeWidth={2.6} />
            Cartons à l&apos;unité
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Chrono logistique ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Chrono logistique
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Chrono logistique : optimisez chaque minute et gagnez un
              temps précieux
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Dans la préparation d&apos;un déménagement, le temps est
              une ressource précieuse qui s&apos;échappe très vite. En
              confiant la livraison de vos fournitures à notre service
              ovillois et en utilisant des emballages aux dimensions
              standardisées, vous reprenez le contrôle de votre
              calendrier. Vous supprimez définitivement les
              allers-retours épuisants dans les grandes surfaces et
              l&apos;effort de charger des paquets encombrants dans
              votre véhicule personnel. Nos cartons dotés de poignées
              ergonomiques renforcées se plient et se sécurisent en un
              tour de main, accélérant la mise en boîte de chaque
              pièce de manière spectaculaire.
            </p>
            <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Ce gain de temps se répercute directement le jour de la
              manutention. Les formats calibrés de nos boîtes
              permettent aux équipes de transport de réaliser un
              empilage parfaitement rectiligne et stable dans le
              camion, maximisant le volume utile sans espaces vides.
              Au déballage, le repérage est tout aussi instantané,
              ce qui vous permet de vider vos colis à toute vitesse et
              de savourer pleinement vos premiers instants dans votre
              nouvelle demeure {city.aCity}.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/cartons-demenagement"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
                data-testid="city-houilles-cta-shop-bottom"
              >
                <Boxes className="h-4 w-4" strokeWidth={2.6} />
                Voir toute la gamme
                <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
              </Link>
              <Link
                to="/livraison"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
                data-testid="city-houilles-cta-livraison"
              >
                <Truck className="h-4 w-4" strokeWidth={2.6} />
                Nos modalités de livraison
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7 space-y-4">
              <div className="flex items-center gap-3">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Timer className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                  Le temps que vous gagnez
                </h3>
              </div>
              <div className="space-y-2.5 text-[14px] text-[#3a4856]">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Zéro aller-retour magasin
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Poignées ergonomiques renforcées
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Pliage en un tour de main
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Empilage rectiligne dans le camion
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                  Repérage instantané au déballage
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — Zones de livraison Yvelines ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Couverture {city.deptCode}
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Nos cartons de déménagement disponibles dans les Yvelines
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Que vous résidiez dans une grande ville ou une commune plus
            résidentielle des Yvelines, nous mettons à votre disposition
            nos cartons de déménagement et fournitures
            d&apos;emballage. Notre couverture départementale nous
            permet de répondre rapidement à vos besoins et de vous
            fournir le matériel nécessaire pour préparer votre
            déménagement dans les meilleures conditions.
          </p>
          <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Découvrez les principales communes du 78 où nos produits
            sont régulièrement livrés à proximité de {city.name}&nbsp;:
          </p>
        </div>

        <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2.5 text-[15px] md:text-base">
          {city.nearbyCities.map((name) => {
            const idSlug = cityNameToSlug(name);
            const target = getYvelinesCityPageBySlug(idSlug);
            return (
              <li key={name}>
                {target ? (
                  <Link
                    to={`/carton-demenagement-${target.slug}`}
                    className="inline-flex items-center font-semibold text-[#237cb5] underline underline-offset-4 decoration-[#237cb5]/40 hover:decoration-[#237cb5] hover:text-[#0f1d2b] transition-colors"
                    data-testid={`city-houilles-nearby-${idSlug}`}
                  >
                    Carton déménagement {name}
                  </Link>
                ) : (
                  <span
                    className="inline-flex items-center font-semibold text-[#5b6b7a]"
                    data-testid={`city-houilles-nearby-${idSlug}`}
                  >
                    Carton déménagement {name}
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        <div className="mt-8">
          <Link
            to={`/cartons-demenagement/${city.deptSlug}`}
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-houilles-cta-back-dept"
          >
            <Package className="h-4 w-4" strokeWidth={2.6} />
            Voir la page {city.deptName} ({city.deptCode})
          </Link>
        </div>
      </div>
    </section>
  </>
);

export default { Sections };
